// ============================================================================
// WorldRail - persistent right-side rail for in-world state surfacing.
//
// The rail owns character awareness for the play surface: active scene first,
// the rest of the cast beneath it, and click-through dossiers that blend
// live entity state with scaffolded character detail.
// ============================================================================

import { useEffect, useMemo, useRef, useState } from 'react';
import { useIsMobile } from '../hooks';
import { useActiveWorldStore } from '../store';
import { Character } from '../types';

interface ChapterRow {
  chapterNumber: number;
  title: string;
  primaryLocation: string | null;
  status: string;
}

interface WorldRailProps {
  activeCharacters: Character[];
}

type RosterSectionKey = 'scene' | 'elsewhere';

const DEFAULT_ROSTER_SECTIONS: Record<RosterSectionKey, boolean> = {
  scene: true,
  elsewhere: false,
};

export function WorldRail({ activeCharacters }: WorldRailProps) {
  const isMobile = useIsMobile();
  const worldFacts = useActiveWorldStore((s) => s.worldFacts);
  const recentBeats = useActiveWorldStore((s) => s.recentBeats);
  const chapters = useActiveWorldStore((s) => s.chapters);
  const currentChapterNumber = useActiveWorldStore((s) => s.currentChapterNumber);
  const glowingChapters = useActiveWorldStore((s) => s.glowingChapters);
  const [selectedCharacterId, setSelectedCharacterId] = useState<string | null>(null);
  const [openRosterSections, setOpenRosterSections] = useState(DEFAULT_ROSTER_SECTIONS);

  const sortedCharacters = useMemo(
    () => sortCharactersForRoster(activeCharacters),
    [activeCharacters],
  );
  const sceneCharacters = useMemo(
    () => sortedCharacters.filter((c) => c.is_player_character || c.is_active),
    [sortedCharacters],
  );
  const elsewhereCharacters = useMemo(
    () => sortedCharacters.filter((c) => !c.is_player_character && !c.is_active),
    [sortedCharacters],
  );
  const selectedCharacter =
    sortedCharacters.find((c) => c.id === selectedCharacterId) ?? null;

  useEffect(() => {
    if (!selectedCharacterId) return;
    if (!activeCharacters.some((c) => c.id === selectedCharacterId)) {
      setSelectedCharacterId(null);
    }
  }, [activeCharacters, selectedCharacterId]);

  if (isMobile) return null;

  const toggleRosterSection = (section: RosterSectionKey) => {
    setOpenRosterSections((current) => ({
      ...current,
      [section]: !current[section],
    }));
  };

  return (
    <aside
      className="w-80 shrink-0 bg-dark-800 border-l border-dark-500 flex flex-col h-full"
      aria-label="World state rail"
    >
      <RailSection
        title={selectedCharacter ? 'Character dossier' : `Characters (${sortedCharacters.length})`}
        className="flex-1 min-h-0 overflow-y-auto"
      >
        {selectedCharacter ? (
          <CharacterDetailPanel
            key={selectedCharacter.id}
            character={selectedCharacter}
            onBack={() => setSelectedCharacterId(null)}
          />
        ) : (
          <div className="space-y-3">
            {sortedCharacters.length === 0 ? (
              <p className="text-xs text-dark-500 italic">
                Characters will appear here as they enter the story.
              </p>
            ) : (
              <>
                <CharacterRosterSection
                  title="Active scene"
                  count={sceneCharacters.length}
                  isOpen={openRosterSections.scene}
                  onToggle={() => toggleRosterSection('scene')}
                >
                  {sceneCharacters.length > 0 ? (
                    <div className="space-y-2">
                      {sceneCharacters.map((c) => (
                        <CharacterRosterItem
                          key={c.id}
                          character={c}
                          onSelect={() => setSelectedCharacterId(c.id)}
                        />
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-dark-500 italic">No characters in scene</p>
                  )}
                </CharacterRosterSection>

                <CharacterRosterSection
                  title="Elsewhere"
                  count={elsewhereCharacters.length}
                  isOpen={openRosterSections.elsewhere}
                  onToggle={() => toggleRosterSection('elsewhere')}
                >
                  {elsewhereCharacters.length > 0 ? (
                    <div className="space-y-2">
                      {elsewhereCharacters.map((c) => (
                        <CharacterRosterItem
                          key={c.id}
                          character={c}
                          onSelect={() => setSelectedCharacterId(c.id)}
                        />
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-dark-500 italic">No known characters elsewhere</p>
                  )}
                </CharacterRosterSection>
              </>
            )}
          </div>
        )}
      </RailSection>

      <WorldHeartbeatSection
        plotThread={worldFacts.activePlotThread}
        recentBeats={recentBeats.events}
      />

      <ChapterSpineSection
        chapters={chapters}
        currentChapterNumber={currentChapterNumber}
        glowingChapters={glowingChapters}
      />
    </aside>
  );
}

interface ChapterSpineProps {
  chapters: ChapterRow[];
  currentChapterNumber: number | null;
  glowingChapters: Set<number>;
}

/**
 * Vertical timeline of chapters. Filled dot = current chapter; hollow dot =
 * history. Recall glow briefly marks chapters surfaced by retrieval.
 */
function ChapterSpineSection({
  chapters,
  currentChapterNumber,
  glowingChapters,
}: ChapterSpineProps) {
  const currentRef = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (currentRef.current) {
      currentRef.current.scrollIntoView({ block: 'nearest', behavior: 'auto' });
    }
  }, [currentChapterNumber]);

  if (chapters.length === 0) return null;

  const ordered = [...chapters].sort((a, b) => b.chapterNumber - a.chapterNumber);

  return (
    <RailSection
      title={`Chapters (${chapters.length})`}
      className="shrink-0 border-t border-dark-500 max-h-[24vh] overflow-y-auto"
    >
      <ul className="relative">
        <li
          aria-hidden="true"
          className="absolute left-[5px] top-1.5 bottom-1.5 w-px bg-dark-500"
        />
        {ordered.map((c) => {
          const isCurrent = c.chapterNumber === currentChapterNumber;
          const isGlowing = glowingChapters.has(c.chapterNumber);
          return (
            <li
              key={c.chapterNumber}
              ref={isCurrent ? currentRef : undefined}
              className="relative pl-4 py-1 flex items-baseline gap-2"
            >
              <span
                className={`absolute left-0 top-2 w-[11px] h-[11px] rounded-full border transition-all duration-300 ${
                  isCurrent
                    ? 'bg-primary-400 border-primary-400'
                    : 'bg-dark-800 border-dark-400'
                } ${
                  isGlowing
                    ? 'shadow-[0_0_0_4px_rgba(96,165,250,0.35)] border-blue-400'
                    : ''
                }`}
                aria-hidden="true"
              />
              <span
                className={`text-[11px] font-mono tabular-nums shrink-0 ${
                  isCurrent ? 'text-dark-50' : 'text-dark-400'
                }`}
              >
                {c.chapterNumber}
              </span>
              <span
                className={`text-xs truncate ${
                  isCurrent ? 'text-dark-100 font-medium' : 'text-dark-400'
                }`}
                title={c.primaryLocation || c.title}
              >
                {c.primaryLocation || c.title}
              </span>
            </li>
          );
        })}
      </ul>
    </RailSection>
  );
}

interface WorldHeartbeatProps {
  plotThread: string | null;
  recentBeats: string[];
}

function WorldHeartbeatSection({ plotThread, recentBeats }: WorldHeartbeatProps) {
  if (!plotThread && recentBeats.length === 0) return null;

  return (
    <RailSection
      title="World"
      className="shrink-0 border-t border-dark-500 max-h-[28vh] overflow-y-auto"
    >
      {plotThread && (
        <div className="mb-3 last:mb-0">
          <div className="text-[10px] uppercase tracking-wide text-dark-400 mb-0.5">
            Plot
          </div>
          <p className="text-xs text-dark-100 leading-snug italic">
            {plotThread}
          </p>
        </div>
      )}

      {recentBeats.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-dark-400 mb-1">
            Recent
          </div>
          <div className="flex flex-wrap gap-1">
            {recentBeats.slice(0, 6).map((tag) => (
              <span
                key={tag}
                className="text-[10px] px-1.5 py-0.5 rounded bg-dark-700 text-dark-300 leading-snug"
                title={tag}
              >
                {tag.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        </div>
      )}
    </RailSection>
  );
}

interface RailSectionProps {
  title: string;
  className?: string;
  children: React.ReactNode;
}

function RailSection({ title, className = '', children }: RailSectionProps) {
  return (
    <section className={`px-4 py-3 ${className}`}>
      <h3 className="text-xs font-semibold text-dark-300 uppercase tracking-wider mb-2">
        {title}
      </h3>
      {children}
    </section>
  );
}

interface CharacterRosterSectionProps {
  title: string;
  count: number;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

function CharacterRosterSection({
  title,
  count,
  isOpen,
  onToggle,
  children,
}: CharacterRosterSectionProps) {
  return (
    <section className="border border-dark-600 rounded-lg overflow-hidden bg-dark-700/60">
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-3 py-2 flex items-center justify-between gap-2 text-left hover:bg-dark-600/40 transition-colors"
        aria-expanded={isOpen}
      >
        <span className="min-w-0">
          <span className="block text-xs font-semibold text-dark-100 uppercase tracking-wide">
            {title}
          </span>
        </span>
        <span className="flex items-center gap-2 shrink-0">
          <span className="text-[11px] text-dark-400 tabular-nums">{count}</span>
          <svg
            className={`w-4 h-4 text-dark-400 transition-transform duration-200 ${
              isOpen ? 'rotate-180' : ''
            }`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </button>
      {isOpen && <div className="px-2 pb-2">{children}</div>}
    </section>
  );
}

interface CharacterRosterItemProps {
  character: Character;
  onSelect: () => void;
}

function CharacterRosterItem({ character, onSelect }: CharacterRosterItemProps) {
  const initial = getInitial(character.name);
  const isInScene = character.is_player_character || character.is_active;
  const subtitle = character.role_in_story || character.identity_summary || 'Character';
  const stateLine =
    character.current_location ||
    (typeof character.last_seen_turn === 'number'
      ? `Last seen turn ${character.last_seen_turn}`
      : 'Location unknown');
  const [pulsingFields, setPulsingFields] = useState<Set<string>>(new Set());
  const prevSnapshot = useRef<{
    emotion: string | null;
    goal: string | null;
    location: string | null;
  } | null>(null);

  useEffect(() => {
    const next = {
      emotion: character.current_emotional_state ?? null,
      goal: character.current_objective ?? null,
      location: character.current_location ?? null,
    };
    const prev = prevSnapshot.current;
    prevSnapshot.current = next;

    if (!prev) return;

    const changed = new Set<string>();
    if (prev.emotion !== next.emotion && next.emotion) changed.add('emotion');
    if (prev.goal !== next.goal && next.goal) changed.add('goal');
    if (prev.location !== next.location && next.location) changed.add('location');

    if (changed.size === 0) return;
    setPulsingFields(changed);
    const t = window.setTimeout(() => setPulsingFields(new Set()), 900);
    return () => window.clearTimeout(t);
  }, [
    character.current_emotional_state,
    character.current_objective,
    character.current_location,
  ]);

  const pulseClass = (key: string) => (pulsingFields.has(key) ? 'animate-field-pulse rounded' : '');

  return (
    <button
      type="button"
      onClick={onSelect}
      className="w-full rounded-md px-2.5 py-2 text-left bg-dark-700 hover:bg-dark-600/70 border border-dark-600/80 transition-colors"
    >
      <div className="flex items-start gap-2">
        <div
          className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${
            isInScene ? 'bg-primary-600 text-white' : 'bg-dark-600 text-dark-200'
          }`}
        >
          {initial}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-sm font-medium text-dark-50 truncate">
              {character.name}
            </span>
            {character.is_player_character && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary-500/20 text-primary-300 shrink-0">
                You
              </span>
            )}
          </div>
          <div className="text-xs text-dark-400 truncate leading-tight">
            {subtitle}
          </div>
          <div className={`text-xs text-dark-300 truncate leading-tight mt-0.5 px-0.5 -mx-0.5 ${pulseClass('location')}`}>
            {stateLine}
          </div>
          {character.current_emotional_state && (
            <div className={`text-[11px] text-dark-400 truncate leading-tight mt-0.5 px-0.5 -mx-0.5 italic ${pulseClass('emotion')}`}>
              {character.current_emotional_state}
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

interface CharacterDetailPanelProps {
  character: Character;
  onBack: () => void;
}

function CharacterDetailPanel({ character, onBack }: CharacterDetailPanelProps) {
  const isInScene = character.is_player_character || character.is_active;

  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={onBack}
        className="text-xs text-dark-300 hover:text-dark-50 inline-flex items-center gap-1"
      >
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to roster
      </button>

      <div className="border border-dark-600 rounded-lg bg-dark-700/70 overflow-hidden">
        <div className="p-3 border-b border-dark-600">
          <div className="flex items-start gap-2">
            <div
              className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold shrink-0 ${
                isInScene ? 'bg-primary-600 text-white' : 'bg-dark-600 text-dark-200'
              }`}
            >
              {getInitial(character.name)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-base font-semibold text-dark-50 leading-tight">
                {character.name}
              </div>
              <div className="flex flex-wrap gap-1 mt-1">
                {character.is_player_character && <Badge tone="primary">You</Badge>}
                {isInScene && <Badge tone="green">In scene</Badge>}
                {character.role_in_story && <Badge>{character.role_in_story}</Badge>}
                {character.age && <Badge>Age: {character.age}</Badge>}
              </div>
            </div>
          </div>
          {character.identity_summary && (
            <p className="text-xs text-dark-300 leading-snug mt-2">
              {character.identity_summary}
            </p>
          )}
        </div>

        <div className="p-3 space-y-2">
          <DetailSection
            title="Live state"
            defaultOpen
            items={[
              { label: 'Location', value: character.current_location },
              { label: 'Objective', value: character.current_objective },
              { label: 'Emotional state', value: character.current_emotional_state },
              { label: 'Active concerns', value: character.active_concerns, variant: 'list' },
              { label: 'Current wounds', value: character.current_wounds, variant: 'list' },
              { label: 'Current secrets', value: character.current_secrets, variant: 'list' },
              { label: 'Last seen', value: formatTurn(character.last_seen_turn) },
              { label: 'Scene count', value: formatSceneCount(character.scene_count) },
            ]}
          />

          <DetailSection
            title="Identity"
            defaultOpen
            items={[
              { label: 'Role', value: character.role_in_story },
              { label: 'Physical description', value: character.physical_description },
              { label: 'Background', value: character.background },
              { label: 'Movement', value: character.movement_quality },
            ]}
          />

          <DetailSection
            title="Voice"
            items={[
              { label: 'Speech patterns', value: character.speech_patterns },
              { label: 'Vocabulary', value: character.vocabulary_tier },
              { label: 'Tempo', value: character.speech_tempo },
              { label: 'Baseline', value: character.emotional_baseline },
              { label: 'Examples', value: character.canonical_dialogue_examples, variant: 'list' },
            ]}
          />

          <DetailSection
            title="Psychology"
            items={[
              { label: 'Primary drive', value: character.primary_drive },
              { label: 'Core wound', value: character.core_wound },
              { label: 'Defense mechanisms', value: character.defense_mechanisms, variant: 'list' },
              { label: 'Blind spots', value: character.blind_spots, variant: 'list' },
              { label: 'Stress response', value: character.stress_response },
              { label: 'Intelligence', value: character.intelligence_signature },
              { label: 'Decision architecture', value: character.decision_architecture },
            ]}
          />

          <DetailSection
            title="Social"
            items={[
              { label: 'Attachment', value: character.attachment_style },
              { label: 'Power orientation', value: character.power_orientation },
              { label: 'Trust calculus', value: character.trust_calculus },
              { label: 'Social calibration', value: character.social_calibration },
            ]}
          />

          <DetailSection
            title="Goals"
            items={[
              { label: 'Surface goals', value: character.surface_goals, variant: 'list' },
              { label: 'Hidden goals', value: character.hidden_goals, variant: 'list' },
            ]}
          />

          <DetailSection
            title="Knowledge and habits"
            items={[
              { label: 'Knowledge domains', value: character.knowledge_domains, variant: 'chips' },
              { label: 'Observable habits', value: character.observable_habits, variant: 'list' },
            ]}
          />
        </div>
      </div>
    </div>
  );
}

type DetailItem = {
  label: string;
  value: string | string[] | number | null | undefined;
  variant?: 'text' | 'list' | 'chips';
};

interface DetailSectionProps {
  title: string;
  items: DetailItem[];
  defaultOpen?: boolean;
}

function DetailSection({ title, items, defaultOpen = false }: DetailSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  const visibleItems = items.filter((item) => hasDetailValue(item.value));

  if (visibleItems.length === 0) return null;

  return (
    <section className="border border-dark-600 rounded-md overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-2.5 py-2 flex items-center justify-between gap-2 text-left bg-dark-700 hover:bg-dark-600/50 transition-colors"
        aria-expanded={open}
      >
        <span className="text-[11px] font-semibold uppercase tracking-wide text-dark-200">
          {title}
        </span>
        <svg
          className={`w-3.5 h-3.5 text-dark-400 transition-transform duration-200 ${
            open ? 'rotate-180' : ''
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="px-2.5 py-2 space-y-2 bg-dark-800/50 border-t border-dark-600">
          {visibleItems.map((item) => (
            <DetailItemView key={item.label} item={item} />
          ))}
        </div>
      )}
    </section>
  );
}

function DetailItemView({ item }: { item: DetailItem }) {
  const value = item.value;

  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-dark-500 mb-0.5">
        {item.label}
      </div>
      {Array.isArray(value) ? (
        item.variant === 'chips' ? (
          <div className="flex flex-wrap gap-1">
            {value.map((entry) => (
              <span
                key={entry}
                className="text-[10px] px-1.5 py-0.5 rounded bg-dark-700 text-dark-200 leading-snug"
              >
                {entry}
              </span>
            ))}
          </div>
        ) : (
          <ul className="list-disc pl-4 space-y-1 text-xs text-dark-200 leading-snug">
            {value.map((entry) => (
              <li key={entry}>{entry}</li>
            ))}
          </ul>
        )
      ) : (
        <div className="text-xs text-dark-200 leading-snug">
          {value}
        </div>
      )}
    </div>
  );
}

function Badge({
  children,
  tone = 'neutral',
}: {
  children: React.ReactNode;
  tone?: 'neutral' | 'primary' | 'green';
}) {
  const toneClass =
    tone === 'primary'
      ? 'bg-primary-500/20 text-primary-300'
      : tone === 'green'
        ? 'bg-green-500/15 text-green-300'
        : 'bg-dark-600 text-dark-300';

  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded leading-tight ${toneClass}`}>
      {children}
    </span>
  );
}

function sortCharactersForRoster(characters: Character[]): Character[] {
  return [...characters].sort((a, b) => {
    if (a.is_player_character !== b.is_player_character) {
      return a.is_player_character ? -1 : 1;
    }

    const aActive = a.is_active ?? false;
    const bActive = b.is_active ?? false;
    if (aActive !== bActive) return aActive ? -1 : 1;

    const aLastSeen = a.last_seen_turn ?? -1;
    const bLastSeen = b.last_seen_turn ?? -1;
    if (aLastSeen !== bLastSeen) return bLastSeen - aLastSeen;

    const aSceneCount = a.scene_count ?? 0;
    const bSceneCount = b.scene_count ?? 0;
    if (aSceneCount !== bSceneCount) return bSceneCount - aSceneCount;

    return a.name.localeCompare(b.name);
  });
}

function hasDetailValue(value: DetailItem['value']): boolean {
  if (Array.isArray(value)) return value.some((entry) => entry.trim().length > 0);
  if (typeof value === 'number') return true;
  return typeof value === 'string' && value.trim().length > 0;
}

function formatTurn(turn: number | null | undefined): string | null {
  return typeof turn === 'number' ? `Turn ${turn}` : null;
}

function formatSceneCount(count: number | null | undefined): string | null {
  if (typeof count !== 'number') return null;
  return `${count} scene${count === 1 ? '' : 's'}`;
}

function getInitial(name: string): string {
  return name.trim().charAt(0).toUpperCase() || '?';
}
